/**
 * Server function for a representative's organization "Overview". It
 * resolves the organization from the URL slug among the organizations the
 * signed-in person represents. Rules live in `~/lib/server/cco-overview`.
 *
 * Aliases: organization dashboard, organization overview
 */

import { queryOptions } from "@tanstack/react-query";
import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { authMiddleware } from "~/lib/auth-middleware";
import type { OrganizationOverview } from "~/lib/shared/cco-overview";

/** The overview, or null for someone who doesn't represent the organization. */
export const getOrganizationOverviewFn = createServerFn({ method: "GET" })
  .middleware([authMiddleware])
  .validator(z.object({ slug: z.string().min(1) }))
  .handler(async ({ data, context }): Promise<OrganizationOverview | null> => {
    const { getMyOrganization } =
      await import("~/lib/server/cco-representatives");
    const organization = await getMyOrganization({
      userId: context.user.id,
      slug: data.slug,
    });
    if (!organization) return null;
    const { getOrganizationOverview } =
      await import("~/lib/server/cco-overview");
    return getOrganizationOverview({
      ccoId: organization.ccoId,
      viewer: {
        userId: context.user.id,
        clerkUserId: context.user.clerkUserId,
      },
    });
  });

export const organizationOverviewQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["organization-overview", slug],
    queryFn: () => getOrganizationOverviewFn({ data: { slug } }),
  });
